import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiFetch } from '../api/client.js';
import { usePrices, formatPrice } from '../hooks/usePrices.js';
import SubscriptionBadge from '../components/SubscriptionBadge.jsx';
import PageMeta from '../components/PageMeta.jsx';

export default function BillingPage() {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [billingCycle, setBillingCycle] = useState('month');
  const { monthly, annual, loading: pricesLoading } = usePrices();

  const selected = billingCycle === 'year' ? annual : monthly;

  // Stripe redirects back with ?success=1 or ?cancelled=1 — read once then clear
  const stripeSuccess  = searchParams.get('success')   === '1';
  const stripeCancelled = searchParams.get('cancelled') === '1';

  useEffect(() => {
    if (stripeSuccess || stripeCancelled) {
      // Remove query params so a page refresh doesn't re-show the banner
      setSearchParams({}, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    apiFetch('/billing/status')
      .then((data) => setSubscription(data))
      .catch((err) => setError(err.message || 'Failed to load billing status'))
      .finally(() => setLoading(false));
  }, []);

  async function handleCheckout() {
    if (!selected) return;
    try {
      setActionLoading(true);
      setError(null);
      const data = await apiFetch('/billing/checkout', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ priceId: selected.id }),
      });
      window.location.href = data.url;
    } catch (err) {
      setError(err.message || 'Failed to start checkout');
      setActionLoading(false);
    }
  }

  async function handlePortal() {
    try {
      setActionLoading(true);
      setError(null);
      const data = await apiFetch('/billing/portal', { method: 'POST' });
      window.location.href = data.url;
    } catch (err) {
      setError(err.message || 'Could not open billing portal');
      setActionLoading(false);
    }
  }

  if (loading) {
    return (
      <>
        <PageMeta
          title="Billing"
          description="Manage your Clawdius subscription. Subscribe, renew, or cancel via the billing portal."
          path="/billing"
        />
        <main id="main-content" className="page-centered">
          <div className="spinner" role="status" aria-label="Loading billing status" />
        </main>
      </>
    );
  }

  // Derive display plan from subscription + live prices
  const planLabel = subscription?.active && monthly
    ? `${monthly.currency.toUpperCase()} ${formatPrice(monthly.amount)}/mo`
    : null;

  return (
    <>
      <PageMeta
        title="Billing"
        description="Manage your Clawdius subscription. Subscribe, renew, or cancel via the billing portal."
        path="/billing"
      />

      <main id="main-content" className="page">
        <h1 className="page-title">Billing</h1>

        {stripeSuccess && (
          <div className="alert alert-success" role="status">
            Subscription activated — welcome to Clawdius!
          </div>
        )}

        {stripeCancelled && (
          <div className="alert alert-info" role="status">
            Checkout cancelled. You have not been charged.
          </div>
        )}

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
            <button className="btn-link" onClick={() => setError(null)} style={{ marginLeft: '0.75rem' }}
              aria-label="Dismiss error message">
              Dismiss
            </button>
          </div>
        )}

        <div className="card">
          <div className="card-row">
            <span className="card-label" id="status-label">Status</span>
            <SubscriptionBadge active={subscription?.active} expiresAtMs={subscription?.expiresAtMs} />
          </div>
          <div className="card-row">
            <span className="card-label">Plan</span>
            <span>{planLabel || 'Clawdius Monthly'}</span>
          </div>
          {subscription?.expiresAtMs && (
            <div className="card-row">
              <span className="card-label">Renews</span>
              <span>{new Date(subscription.expiresAtMs).toLocaleDateString()}</span>
            </div>
          )}
        </div>

        <div className="action-row">
          {subscription?.active ? (
            <button
              className="btn btn-secondary"
              onClick={handlePortal}
              disabled={actionLoading}
              aria-busy={actionLoading}
            >
              {actionLoading ? 'Redirecting\u2026' : 'Manage Subscription'}
            </button>
          ) : (
            <div className="billing-subscribe-section">
              {/* Billing cycle selector */}
              {annual && !pricesLoading && (
                <div className="pricing-toggle pricing-toggle-sm" role="radiogroup" aria-label="Billing cycle">
                  <button
                    role="radio"
                    aria-checked={billingCycle === 'month'}
                    className={`pricing-toggle-btn${billingCycle === 'month' ? ' active' : ''}`}
                    onClick={() => setBillingCycle('month')}
                  >
                    Monthly
                  </button>
                  <button
                    role="radio"
                    aria-checked={billingCycle === 'year'}
                    className={`pricing-toggle-btn${billingCycle === 'year' ? ' active' : ''}`}
                    onClick={() => setBillingCycle('year')}
                  >
                    Annual{annual.savings > 0 ? ` (${annual.savings}% off)` : ''}
                  </button>
                </div>
              )}

              <button
                className="btn btn-primary"
                onClick={handleCheckout}
                disabled={actionLoading || pricesLoading || !selected}
                aria-busy={actionLoading}
              >
                {actionLoading
                  ? 'Redirecting\u2026'
                  : selected
                    ? `Subscribe — ${formatPrice(selected.amount)}/${selected.interval === 'year' ? 'yr' : 'mo'}`
                    : 'Subscribe'}
              </button>
            </div>
          )}
        </div>
      </main>
    </>
  );
}
